import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import InfoTile from 'flarum/common/components/InfoTile';
import Input from 'flarum/common/components/Input';
import Switch from 'flarum/common/components/Switch';
import extractText from 'flarum/common/utils/extractText';
import ItemList from 'flarum/common/utils/ItemList';
import KeyCombo from '../../common/components/KeyCombo';
import { GROUP_ORDER, PREFERENCES, trans, transIfExists } from '../../common/config';
import CustomizeShortcutsModal from './CustomizeShortcutsModal';
/**
 * The cheat sheet: every shortcut available to this user right now, grouped by
 * where it applies.
 *
 * It lists what is actually live — a shortcut whose extension isn't installed,
 * or that a guest can't use, isn't shown — so the sheet never promises a key
 * that would do nothing.
 *
 * A reference is for scanning, not for stepping through, so the groups sit side
 * by side rather than behind tabs: everything is visible at once on a screen
 * with the room for it, and the filter narrows it when there isn't.
 */
export default class KeyboardShortcutsModal extends Modal {
  /**
   * The current filter query.
   */
  query = '';
  togglingEnabled = false;
  className() {
    return 'KeyboardShortcutsModal Modal--flat Modal--large';
  }
  title() {
    return trans('forum.modal.title');
  }
  content() {
    const groups = this.groups();
    return m("div", {
      className: "Modal-body KeyboardShortcutsModal-body"
    }, this.search(), groups.length ? m("div", {
      className: "KeyboardShortcuts-groups"
    }, groups) : m(InfoTile, {
      icon: "fas fa-keyboard"
    }, trans('forum.modal.no_results', {
      query: this.query
    })));
  }

  /**
   * The modal's own footer, below the body: the controls that act on shortcuts
   * as a whole rather than on any one of them.
   */
  inner() {
    const footer = this.footerItems().toArray();
    return [super.inner(), !!footer.length && m("div", {
      className: "Modal-footer KeyboardShortcutsModal-footer"
    }, footer)];
  }
  search() {
    const label = extractText(trans('forum.modal.filter_placeholder'));
    return m("div", {
      className: "KeyboardShortcutsModal-search"
    }, m(Input, {
      type: "search",
      prefixIcon: "fas fa-search",
      clearable: true,
      clearLabel: extractText(trans('forum.modal.filter_clear_label')),
      "aria-label": label,
      placeholder: label,
      value: this.query,
      onchange: value => {
        this.query = value;
      }
    }));
  }

  /**
   * One section per group that has anything to show.
   */
  groups() {
    const shortcuts = this.visibleShortcuts();
    const groups = [];
    for (const shortcut of shortcuts) {
      if (!groups.includes(shortcut.group)) groups.push(shortcut.group);
    }

    // Known groups keep their canonical order; anything an extension has added
    // follows, in the order it was registered.
    groups.sort((a, b) => {
      const [ia, ib] = [GROUP_ORDER.indexOf(a), GROUP_ORDER.indexOf(b)];
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return groups.map(group => this.group(group, shortcuts.filter(shortcut => shortcut.group === group)));
  }
  group(group, shortcuts) {
    // A description list, not a `Dropdown-menu`: these rows are a reference,
    // not a menu of things to activate, and core's dropdown styles carry
    // behaviour (hidden until open, items sized as targets) that would be
    // wrong here. The section takes the modal's visual language — a divider
    // and a heading in the same voice as `Dropdown-header` — without borrowing
    // a component whose semantics don't fit.
    return m("section", {
      className: `KeyboardShortcuts-group KeyboardShortcuts-group--${group}`,
      key: group
    }, m("hr", {
      className: "Modal-divider"
    }), m("h3", {
      className: "KeyboardShortcuts-groupTitle"
    }, this.groupLabel(group)), m("dl", {
      className: "KeyboardShortcuts-list"
    }, shortcuts.map(shortcut => m("div", {
      className: "KeyboardShortcuts-item",
      key: shortcut.id
    }, m("dt", {
      className: "KeyboardShortcuts-itemLabel"
    }, shortcut.label()), m("dd", {
      className: "KeyboardShortcuts-itemKeys"
    }, m(KeyCombo, {
      binding: app.shortcuts.binding(shortcut.id)
    }))))));
  }
  groupLabel(group) {
    // Groups registered by other extensions carry their own translation key,
    // so fall back to showing the raw group name rather than a missing string.
    return transIfExists(`lib.groups.${group}`, group);
  }

  /**
   * The shortcuts to list: bound, applicable to this user, and matching the
   * filter.
   */
  visibleShortcuts() {
    const query = this.query.trim().toLowerCase();
    return app.shortcuts.all().filter(shortcut => {
      if (shortcut.visible?.() === false) return false;
      const binding = app.shortcuts.binding(shortcut.id);
      if (!binding) return false;
      if (!query) return true;
      const haystack = [extractText(shortcut.label()), binding, extractText(this.groupLabel(shortcut.group))].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }
  footerItems() {
    const items = new ItemList();
    if (app.session.user) {
      items.add('enabled', m(Switch, {
        state: app.shortcuts.enabled(),
        loading: this.togglingEnabled,
        onchange: this.toggleEnabled.bind(this)
      }, trans('forum.modal.enabled_label')), 100);
    }
    if (app.forum.attribute('canCustomizeKeyboardShortcuts')) {
      items.add('customize', m(Button, {
        className: "Button Button--link",
        icon: "fas fa-sliders",
        onclick: () => app.modal.show(CustomizeShortcutsModal)
      }, trans('forum.modal.customize_button')), 90);
    }
    return items;
  }
  toggleEnabled(enabled) {
    if (!app.session.user) return;
    this.togglingEnabled = true;
    app.session.user.savePreferences({
      [PREFERENCES.enabled]: enabled
    }).catch(() => {}).then(() => {
      this.togglingEnabled = false;
      m.redraw();
    });
  }
}
flarum.reg.add('datlechin-keyboard-shortcuts', 'forum/components/KeyboardShortcutsModal', KeyboardShortcutsModal);